import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  initialQuality?: number;
}

/**
 * 画像ファイルを圧縮
 */
export async function compressImage(
  file: File,
  options?: CompressionOptions
): Promise<File> {
  const defaultOptions: CompressionOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    initialQuality: 0.85,
  };

  const finalOptions = { ...defaultOptions, ...options };

  try {
    const compressedFile = await imageCompression(file, finalOptions);
    return compressedFile;
  } catch (error) {
    console.error('画像圧縮エラー:', error);
    throw new Error('画像の圧縮に失敗しました');
  }
}

/**
 * サムネイル生成（300x300px WebP）
 */
export async function generateThumbnail(file: File): Promise<File> {
  const options = {
    maxSizeMB: 0.1, // 100KB以下
    maxWidthOrHeight: 300,
    useWebWorker: true,
    initialQuality: 0.8,
  };

  try {
    const thumbnail = await imageCompression(file, options);

    // WebP形式に変換
    const webpBlob = await convertToWebP(thumbnail);

    return new File(
      [webpBlob],
      file.name.replace(/\.[^.]+$/, '_thumb.webp'),
      { type: 'image/webp' }
    );
  } catch (error) {
    console.error('サムネイル生成エラー:', error);
    throw new Error('サムネイルの生成に失敗しました');
  }
}

/**
 * 画像をWebP形式に変換
 */
async function convertToWebP(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('WebP conversion failed'));
          }
        },
        'image/webp',
        0.8
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('画像の読み込みに失敗しました'));
    };

    img.src = url;
  });
}

/**
 * EXIF情報を削除
 */
export async function stripExifData(file: File): Promise<File> {
  // browser-image-compression はデフォルトでEXIFを削除
  return compressImage(file, {
    maxSizeMB: 10, // 圧縮せず、EXIFのみ削除
    maxWidthOrHeight: 10000,
  });
}
